// ABOUTME: React Query orchestration hook for pump settings entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { usePumpSettingsConfig } from './usePumpSettingsConfig'
import { useCreatePumpSettings, useCreatePumpSettingsWithScope, useUpdatePumpSettings, useDeletePumpSettings } from './usePumpSettingsMutations'
import { PumpSettingsConfig, PumpSettingsFormValues, AvailablePumpFormValues } from '../types/pumpSettings'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

/** Strip _rowId (UI-only) from pumps before equality comparison */
const stripPumpRowIds = (values: PumpSettingsFormValues): PumpSettingsFormValues => ({
  availablePumps: values.availablePumps
    ? {
      ...values.availablePumps,
      pumps: values.availablePumps.pumps.map(({ _rowId: _, ...rest }: AvailablePumpFormValues) => rest),
    }
    : null,
  hoseLength: values.hoseLength,
  defaultAnswers: values.defaultAnswers,
})

const equalsParent = (values: PumpSettingsFormValues, config: PumpSettingsConfig): boolean =>
  JSON.stringify(stripPumpRowIds(values)) === JSON.stringify(stripPumpRowIds(config.parentValues))

interface UsePumpSettingsEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: PumpSettingsFormValues) => void
}

interface UsePumpSettingsEntityResult {
  config: PumpSettingsConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

export const usePumpSettingsEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UsePumpSettingsEntityParams): UsePumpSettingsEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = usePumpSettingsConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    availablePumps: cfg.availablePumps,
    hoseLength: cfg.hoseLength,
    defaultAnswers: cfg.defaultAnswers,
  })), [rawConfig, pendingScope])

  const createMutation = useCreatePumpSettings(countryId, businessLine)
  const createWithScopeMutation = useCreatePumpSettingsWithScope(countryId, businessLine)
  const updateMutation = useUpdatePumpSettings(countryId, businessLine)
  const deleteMutation = useDeletePumpSettings(countryId, businessLine)

  const formValuesRef = useRef<PumpSettingsFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const configRef = useRef<PumpSettingsConfig | undefined>(config)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: PumpSettingsFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
    if (!formValuesRef.current) return
    const values = formValuesRef.current

    const isEmpty = values.availablePumps !== null && values.availablePumps.pumps.length === 0
    const scopeToCreate = pendingScopeRef.current
    const matchesParent = configRef.current !== undefined && (scopeToCreate !== undefined || scopeId !== rootScopeId) && equalsParent(values, configRef.current)

    if (scopeToCreate) {
      if (isEmpty || matchesParent) {
        onSuccess({})
        return
      }
      mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
      return
    }

    if (!scopeId) return

    if (isEmpty || matchesParent) {
      if (configIdRef.current) {
        mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
      } else {
        onSuccess({ wasDelete: true })
      }
      return
    }

    const isNew = !configIdRef.current
    const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
    mutate({ scopeId, values }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
  }, [scopeId, rootScopeId])

  const handleReset = useCallback((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
    if (!scopeId) return
    if (!configIdRef.current) {
      onSuccess({ wasDelete: true })
      return
    }
    mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
  }, [scopeId])

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
